import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { Link } from "react-scroll";
import { useTheme } from "../ThemeContext";
import { useScrollY } from "../hooks/useScrollY";
import { FaBars, FaXmark, FaSun, FaMoon, FaHouse, FaUser, FaCode, FaStar, FaNewspaper, FaEnvelope } from "react-icons/fa6";
import { BRAND_NAME } from "../services/seedData";

const _dotIdx = BRAND_NAME.indexOf(".");
const BRAND_PREFIX = _dotIdx >= 0 ? BRAND_NAME.slice(0, _dotIdx) : BRAND_NAME;
const BRAND_SUFFIX = _dotIdx >= 0 ? BRAND_NAME.slice(_dotIdx) : "";

const navLinks = [
  { name: "Home", to: "home", icon: FaHouse },
  { name: "About", to: "about", icon: FaUser },
  { name: "Portfolio", to: "portfolio", icon: FaCode },
  { name: "Testimonials", to: "testimonials", icon: FaStar },
  { name: "Blog", to: "blog", icon: FaNewspaper },
  { name: "Contact", to: "contact", icon: FaEnvelope },
];

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const scrollY = useScrollY();
  const scrolled = scrollY > 60;
  const { theme, toggleTheme } = useTheme();

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  // Close on Escape key
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Render overlay via portal so it isn't affected by the navbar's
  // backdrop-filter / transform containing-block
  const mobileMenu = ReactDOM.createPortal(
    <div
      className={`nav-mobile-overlay${open ? " open" : ""}`}
      aria-hidden={!open}
      onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
    >
      {/* Close button */}
      <button
        className="nav-mobile-close"
        onClick={() => setOpen(false)}
        aria-label="Close menu"
      >
        <FaXmark />
      </button>

      {/* Brand */}
      <span className="nav-mobile-brand">
        {BRAND_PREFIX}<span>{BRAND_SUFFIX}</span>
      </span>

      {/* Accent divider */}
      <div className="nav-mobile-divider" />

      {/* Nav links */}
      <ul className="nav-mobile-links">
        {navLinks.map(({ name, to, icon: Icon }, i) => (
          <li key={to} style={{ "--i": i }}>
            <Link
              to={to}
              href={`#${to}`}
              smooth
              duration={500}
              spy
              activeClass="mobile-active"
              offset={-80}
              onClick={() => setOpen(false)}
            >
              <span className="nav-mobile-link-btn"><Icon className="nav-mobile-link-icon" />{name}</span>
            </Link>
          </li>
        ))}
      </ul>

      {/* Theme toggle */}
      <button
        className="nav-mobile-theme"
        onClick={toggleTheme}
        aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
      >
        <span className="theme-icon">{theme === "dark" ? <FaSun /> : <FaMoon />}</span>
        {theme === "dark" ? "Light Mode" : "Dark Mode"}
      </button>
    </div>,
    document.body
  );

  return (
    <>
      <nav className={`space-navbar ${scrolled ? "scrolled" : ""}`}>
        <div className="nav-inner">
          {/* Logo */}
          <Link to="home" href="#home" smooth duration={500} style={{ cursor: "pointer" }} onClick={() => setOpen(false)}>
            <span className="nav-logo">
              {BRAND_PREFIX}<span style={{ color: "var(--cyan)" }}>{BRAND_SUFFIX}</span>
            </span>
          </Link>

          {/* Desktop links */}
          <ul className="nav-links">
            {navLinks.map(({ name, to }) => (
              <li key={to}>
                <Link
                  to={to}
                  href={`#${to}`}
                  smooth
                  duration={500}
                  spy
                  activeClass="active-link"
                  offset={-80}
                >
                  <span className="nav-link-btn">{name}</span>
                </Link>
              </li>
            ))}
          </ul>

          {/* Actions */}
          <div className="nav-actions">
            <button
              className="theme-toggle"
              onClick={toggleTheme}
              aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
            >
              <span className="theme-icon">{theme === "dark" ? <FaSun /> : <FaMoon />}</span>
            </button>
            <button
              className="nav-toggle"
              onClick={() => setOpen((o) => !o)}
              aria-label="Toggle menu"
              aria-expanded={open}
            >
              <FaBars />
            </button>
          </div>
        </div>
      </nav>
      {mobileMenu}
    </>
  );
};

export default Navbar;